import { CSSProperties, useLayoutEffect, useRef, useState } from 'react';
import { Link, Text } from '@backstage/ui';
import styles from './ExpandableText.module.css';

interface ExpandableTextProps {
  text: string;
  /** Lines shown while collapsed. */
  lines?: number;
}

export const ExpandableText = ({ text, lines = 2 }: ExpandableTextProps) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Re-measure on resize, since column widths change the wrapping. Expanded text never overflows, so keep the last
  // collapsed result while expanded.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return undefined;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, lines, expanded]);

  const toggle = (
    <Link
      className={expanded ? undefined : styles.toggle}
      onPress={() => setExpanded(!expanded)}
    >
      {expanded ? 'Show less' : 'Show more'}
    </Link>
  );

  return (
    <div className={styles.wrapper}>
      <Text
        as="p"
        ref={ref}
        className={expanded ? undefined : styles.clamped}
        style={{ '--expandable-text-lines': lines } as CSSProperties}
      >
        {text}
        {expanded && <>{'\u00a0'.repeat(4)}{toggle}</>}
      </Text>
      {!expanded && overflows && toggle}
    </div>
  );
};
